// Per-plane shape data: [centroid-x, centroid-y, nose-correction-degrees]
// All planes are drawn with the nose pointing right, so correction just accounts
// for the natural upper-right tilt of each shape's nose relative to its centroid.
const PLANE_DATA = [
  [375, 133, 37],  // plane 0: top-right cluster
  [ 65, 258,  0],  // plane 1: left group — nose faces right, no correction needed
  [336, 353, 43],  // plane 2: bottom-right cluster
  [112,  64, 30],  // plane 3: upper-left shape (new)
  [170, 460, 26],  // plane 4: bottom-center shape (new)
];

// Scroll-progress offset at which each plane begins moving.
const STAGGER = [0, 0.10, 0.20, 0.30, 0.40];

class PaperPlanes extends HTMLElement {
  connectedCallback() {
    this._stage = this.querySelector('.pp-stage');

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      this._init();
    } else {
      window.addEventListener('load', () => this._init(), { once: true });
    }

    this._onResize = this._debounce(() => {
      this._cleanup();
      this._init();
    }, 250);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    this._cleanup();
    window.removeEventListener('resize', this._onResize);
  }

  _debounce(fn, ms) {
    let t;
    return () => { clearTimeout(t); t = setTimeout(() => fn.call(this), ms); };
  }

  _cleanup() {
    if (this._st) { this._st.kill(); this._st = null; }
    if (this._stVis) { this._stVis.kill(); this._stVis = null; }
    this._stage?.classList.remove('is-visible');
    this._planes?.forEach((p) => p.removeAttribute('transform'));
    this._planes = null;
    this.style.height = '';
  }

  _init() {
    gsap.registerPlugin(ScrollTrigger);

    const isMobile = window.innerWidth < 768;
    const scale    = isMobile ? 0.04 : 0.055;
    const count    = isMobile ? 2 : 5;

    // Set section height in real pixels so it's accurate on mobile
    // (avoids 100vh = initial viewport including hidden browser chrome)
    const factor = parseFloat(this.dataset.heightFactor) || 4;
    this.style.height = (window.innerHeight * factor) + 'px';

    // Apply corrective SVG transforms to each visible plane shape
    const allPlanes = Array.from(this._stage.querySelectorAll('.pp-plane'));
    allPlanes.forEach((planeEl, i) => {
      const active = i < count;
      planeEl.style.display = active ? '' : 'none';
      if (!active) return;
      const [cx, cy, correction] = PLANE_DATA[i];
      planeEl.querySelector('.pp-shape')
        .setAttribute('transform', `scale(${scale}) rotate(${correction}) translate(${-cx},${-cy})`);
    });

    this._planes = allPlanes.slice(0, count);

    const svg = this._stage.querySelector('.pp-svg');
    const pathEls = [
      svg.querySelector('#pp-path-0'),
      svg.querySelector('#pp-path-1'),
      svg.querySelector('#pp-path-2'),
      svg.querySelector('#pp-path-3'),
      svg.querySelector('#pp-path-4'),
    ].slice(0, count);

    const pathLengths = pathEls.map((p) => p.getTotalLength());

    // Show stage only while section is within the viewport
    this._stVis = ScrollTrigger.create({
      trigger: this,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: (self) => this._stage.classList.toggle('is-visible', self.isActive),
    });

    this._st = ScrollTrigger.create({
      trigger: this,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 1,
      onUpdate: (self) => {
        this._planes.forEach((plane, i) => {
          const s   = STAGGER[i];
          const p   = Math.min(1, Math.max(0, (self.progress - s) / (1 - s)));
          const len = pathLengths[i];
          const dist = p * len;
          const pt    = pathEls[i].getPointAtLength(dist);
          const ahead = pathEls[i].getPointAtLength(Math.min(dist + 0.5, len));
          const angle = Math.atan2(ahead.y - pt.y, ahead.x - pt.x) * 180 / Math.PI;
          plane.setAttribute('transform', `translate(${pt.x},${pt.y}) rotate(${angle})`);
          plane.classList.add('is-flying');
        });
      },
    });
  }
}

customElements.define('paper-planes', PaperPlanes);
